/**
 * Kurzer Benachrichtigungs-Ton per Web Audio API (kein Asset noetig).
 *
 * Nur im Browser: In der nativen App uebernimmt der Push des Betriebssystems
 * den Ton. Wird gedrosselt, damit ein Chat-Burst nicht zum Dauerpiepen wird.
 * Vor der ersten Nutzer-Geste blockiert der Browser Audio (Autoplay-Regel) —
 * dann bleibt der Ton still, es fliegt nie ein Fehler.
 */
import { Capacitor } from '@capacitor/core';

const MIN_GAP_MS = 1500;
let audioContext = null;
let lastPlayedAt = 0;

const getAudioContextClass = () => (
  typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null
);

export const playNotificationBeep = (now = Date.now()) => {
  if (Capacitor.isNativePlatform()) return false;
  const AudioContextClass = getAudioContextClass();
  if (!AudioContextClass) return false;
  if (now - lastPlayedAt < MIN_GAP_MS) return false;

  try {
    audioContext = audioContext || new AudioContextClass();
    if (audioContext.state === 'suspended') {
      // resume() liefert ein Promise; ohne Nutzer-Geste bleibt der Context
      // suspendiert. Dann lieber still bleiben als einen Fehler zu riskieren.
      const resumed = audioContext.resume();
      if (resumed && typeof resumed.catch === 'function') resumed.catch(() => {});
      if (audioContext.state === 'suspended') return false;
    }

    const start = audioContext.currentTime;
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(660, start);
    osc.frequency.setValueAtTime(880, start + 0.07);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.08, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
    osc.connect(gain);
    gain.connect(audioContext.destination);
    osc.start(start);
    osc.stop(start + 0.17);

    lastPlayedAt = now;
    return true;
  } catch {
    // Audio nicht verfuegbar — kein Grund, die Benachrichtigung zu verlieren
    return false;
  }
};

/** Nur fuer Tests: Drosselung und Context zuruecksetzen. */
export const resetNotificationSoundForTests = () => {
  audioContext = null;
  lastPlayedAt = 0;
};
